// Uptime monitoring utility functions

export interface UptimeCheck {
  id: string;
  site_id: string;
  checked_at: string;
  status_code: number | null;
  response_time_ms: number | null;
  is_up: boolean;
  error_message: string | null;
}

export interface UptimeIncident {
  id: string;
  site_id: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  response_summary: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface MonitoredSite {
  id: string;
  name: string;
  url: string;
  is_active: boolean | null;
  alerts_snoozed_until: string | null;
  alert_recipients?: string[] | null;
}

// Calculate availability percentage from checks
export function calculateAvailability(checks: UptimeCheck[]): number {
  if (checks.length === 0) return 100;
  const upCount = checks.filter(c => c.is_up).length;
  return Math.round((upCount / checks.length) * 10000) / 100;
}

// Calculate Apdex score (Application Performance Index)
// Satisfied: < threshold, Tolerating: < threshold*4, Frustrated: >= threshold*4
export function calculateApdex(checks: UptimeCheck[], threshold: number = 500): number {
  if (checks.length === 0) return 1;
  
  let satisfied = 0;
  let tolerating = 0;
  
  checks.forEach(check => {
    if (!check.is_up || check.response_time_ms === null) return;
    
    if (check.response_time_ms <= threshold) {
      satisfied++;
    } else if (check.response_time_ms <= threshold * 4) {
      tolerating++;
    }
  });
  
  const apdex = (satisfied + tolerating / 2) / checks.length;
  return Math.round(apdex * 100) / 100;
}

// Calculate total downtime from checks
export function calculateDowntime(checks: UptimeCheck[], intervalMinutes: number = 5): {
  minutes: number;
  percentage: number;
} {
  if (checks.length === 0) return { minutes: 0, percentage: 0 };
  
  const downCount = checks.filter(c => !c.is_up).length;
  const minutes = downCount * intervalMinutes;
  const totalMinutes = checks.length * intervalMinutes;
  const percentage = Math.round((minutes / totalMinutes) * 10000) / 100;
  
  return { minutes, percentage };
}

// Calculate average response time
export function calculateAvgResponseTime(checks: UptimeCheck[]): number {
  const validChecks = checks.filter(c => c.is_up && c.response_time_ms !== null);
  if (validChecks.length === 0) return 0;
  
  const sum = validChecks.reduce((acc, c) => acc + (c.response_time_ms || 0), 0);
  return Math.round(sum / validChecks.length);
}

// Group checks by day for calendar view
export function groupChecksByDay(checks: UptimeCheck[]): Map<string, {
  date: string;
  totalChecks: number;
  upChecks: number;
  availability: number;
  avgResponseTime: number;
}> {
  const grouped = new Map<string, UptimeCheck[]>();
  
  checks.forEach(check => {
    const date = check.checked_at.split('T')[0];
    if (!grouped.has(date)) {
      grouped.set(date, []);
    }
    grouped.get(date)!.push(check);
  });
  
  const result = new Map<string, {
    date: string;
    totalChecks: number;
    upChecks: number;
    availability: number;
    avgResponseTime: number;
  }>();
  
  grouped.forEach((dayChecks, date) => {
    const upChecks = dayChecks.filter(c => c.is_up).length;
    const availability = Math.round((upChecks / dayChecks.length) * 10000) / 100;
    const avgResponseTime = calculateAvgResponseTime(dayChecks);
    
    result.set(date, {
      date,
      totalChecks: dayChecks.length,
      upChecks,
      availability,
      avgResponseTime,
    });
  });
  
  return result;
}

// Get last 24 hours of checks for sparkline
export function getLast24HoursChecks(checks: UptimeCheck[]): UptimeCheck[] {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  
  return checks.filter(c => new Date(c.checked_at) >= yesterday)
    .sort((a, b) => new Date(a.checked_at).getTime() - new Date(b.checked_at).getTime());
}

// Find longest downtime from incidents
export function findLongestDowntime(incidents: UptimeIncident[]): number {
  if (incidents.length === 0) return 0;
  
  return Math.max(...incidents.map(i => i.duration_seconds || 0));
}

// Format duration in human readable format
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
}

// Get status color based on availability
export function getStatusColor(availability: number): string {
  if (availability >= 99.9) return '#22c55e'; // green
  if (availability >= 99) return '#eab308'; // yellow
  return '#ef4444'; // red
}

// Get Apdex label
export function getApdexLabel(apdex: number): string {
  if (apdex >= 0.94) return 'Excellent';
  if (apdex >= 0.85) return 'Good';
  if (apdex >= 0.7) return 'Fair';
  if (apdex >= 0.5) return 'Poor';
  return 'Unacceptable';
}
